/* Money — AI + infra spend. 3 StatCards · spend sparkline · budget gauge ·
   by model · by agent · infrastructure. Anthropic billing is an [I DO] (coming
   soon); infra is real. */
import { Sparkline, BudgetGauge } from '../../ds/mc-kit';
import { StatCard, StatusPill } from '../../ds';
import { useContract } from '../../lib/api';
import { Widget } from '../ui';
import { integrationFor } from '../../lib/flags';
import { deriveMoney } from '../../lib/selectors';

export function Money() {
  const money = useContract('money');
  const budget = useContract('budget');
  const crew = useContract('crew', { pollMs: 0 });
  const connect = integrationFor('money')?.connect;

  const budgetPct = budget.data?.pct ?? 74;
  const budgetPillStatus = budgetPct >= 90 ? 'errored' : budgetPct >= 70 ? 'running' : 'idle';
  const budgetPillLabel = `${budgetPct >= 90 ? 'danger' : budgetPct >= 70 ? 'amber' : 'ok'} · ${budgetPct}%`;

  const moneyDerived = money.data ? deriveMoney(money.data, crew.data ?? []) : null;

  return (
    <div data-screen-label="Money" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>

      {/* === ROW 1: 3 StatCards === */}
      <div className="mc-auto mc-auto-md">
        <StatCard
          label="Spent · June"
          value={moneyDerived ? `$${moneyDerived.moneyTotal}` : '—'}
          delta={budget.data ? `${budget.data.pct}%` : '—'}
          deltaTone="danger"
        />
        <StatCard
          label="Budget cap"
          value={budget.data ? `$${budget.data.cap}` : '—'}
          delta="monthly"
          deltaTone="muted"
        />
        <StatCard
          label="Projected"
          value={budget.data ? `$${budget.data.perDay}` : '—'}
          unit="/ day"
          delta={budget.data ? `${budget.data.daysLeft} days left` : '—'}
          deltaTone="danger"
        />
      </div>

      {/* === ROW 2: spend sparkline (1.5fr) + budget gauge (1fr) === */}
      <div className="mc-row-2" style={{ '--mc-2': '1.5fr 1fr' } as React.CSSProperties}>
        <Widget query={money} title="// spend · 30d" minHeight={120} rows={3} connect={connect} badge="coming soon"
          aside={moneyDerived ? (
            <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 18, color: 'var(--ink)' }}>
              ${moneyDerived.moneyTotal}
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}> models</span>
            </span>
          ) : undefined}
          errorLabel="Billing offline" errorSub="Couldn't reach the usage API." emptyLabel="No spend yet">
          {(m) => <Sparkline data={m.spend30} height={120} tone="iris" />}
        </Widget>

        <Widget query={budget} title="// budget" minHeight={150} rows={3}
          connect={integrationFor('budget')?.connect} badge="coming soon"
          accent={true}
          aside={<StatusPill status={budgetPillStatus} label={budgetPillLabel} dot={false} />}
          errorLabel="Budget unavailable" emptyLabel="No spend this cycle">
          {(b) => <BudgetGauge {...b} />}
        </Widget>
      </div>

      {/* === ROW 3: by model | by agent | infrastructure (3 cols) === */}
      <div className="mc-auto mc-auto-md">
        {/* By model */}
        <Widget query={money} title="// by model" minHeight={150} rows={3} connect={connect} badge="coming soon"
          errorLabel="Billing offline" emptyLabel="No spend yet">
          {(m) => (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {deriveMoney(m, crew.data ?? []).byModel.map((row) => (
                <div key={row.name} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 13, color: 'var(--ink)' }}>{row.name}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>${row.usd} · {row.pct}%</span>
                  </div>
                  <div style={{ height: 7, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${row.pct}%`, borderRadius: 999, background: row.barColor, boxShadow: `0 0 10px ${row.glow}` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Widget>

        {/* By agent */}
        <Widget query={money} title="// by agent" minHeight={150} rows={3} connect={connect} badge="coming soon"
          errorLabel="Billing offline" emptyLabel="No spend yet">
          {(m) => (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {deriveMoney(m, crew.data ?? []).byAgent.slice(0, 6).map((row) => (
                <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  <span style={{ fontSize: 14, width: 18, textAlign: 'center', flex: 'none' }}>{row.glyph}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--ink)', width: 46, flex: 'none' }}>{row.name}</span>
                  <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--surface2)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${row.pct}%`, borderRadius: 999, background: 'var(--primary)' }} />
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--ink)', width: 36, textAlign: 'right', flex: 'none' }}>${row.usd}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>

        {/* Infrastructure */}
        <Widget query={money} title="// infrastructure" minHeight={150} rows={3} connect={connect} badge="connect me"
          errorLabel="Billing offline" emptyLabel="No line items">
          {(m) => (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {m.infra.map((row) => (
                <div key={row.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: 'var(--ink)' }}>{row.name}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' }}>{row.note}</div>
                  </div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink)' }}>${row.usd}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      </div>
    </div>
  );
}
